import { Button } from "@instacover/ui"
import { IconGenericError } from "@instacover/ui/icons"
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router"
import { Trans } from "react-i18next"

import { BaseLayout } from "@/sections/layouts/BaseLayout"
import { BrowserSupport, resolveCurrentBrowserSupport } from "@/utils/browserSupport"
import { markBrowserWarningDismissed } from "@/utils/browserWarningTracker"
import { copyToClipboard } from "@/utils/clipboard"

export const Route = createFileRoute("/$sessionId/_session/unsupportedDevice")({
  component: UnsupportedDeviceRoute,
})

// Reached only through the redirect in the _session loader
function UnsupportedDeviceRoute() {
  const { sessionId } = Route.useParams()
  const navigate = useNavigate()

  const browserSupport = resolveCurrentBrowserSupport()

  // Someone opened the URL by hand on a device that is fine — send them back to the inspection.
  if (browserSupport === BrowserSupport.SUPPORTED) {
    return <Navigate to="/$sessionId" params={{ sessionId }} replace />
  }

  const icon = <IconGenericError className="text-ic-primary mx-auto max-w-87.5" />

  /* desktop blocked */
  if (browserSupport === BrowserSupport.DESKTOP) {
    return (
      <BaseLayout
        aboveHeading={icon}
        heading={{ i18nKey: "unsupported.desktopTitle" }}
        description={{ i18nKey: "unsupported.desktopText", components: { br: <br />, b: <b /> } }}
      />
    )
  }

  /* unsupported browser */
  function handleCopyLink() {
    copyToClipboard(`${window.location.origin}/${sessionId}`)
  }

  function handleContinue() {
    markBrowserWarningDismissed(sessionId)
    navigate({ to: "/$sessionId", params: { sessionId }, replace: true })
  }

  return (
    <BaseLayout
      aboveHeading={icon}
      heading={{ i18nKey: "unsupported.browserTitle" }}
      description={{ i18nKey: "unsupported.browserText", components: { br: <br />, b: <b /> } }}
      buttons={
        <div className="flex w-full flex-col gap-2">
          <Button variant="primary" onClick={handleCopyLink} data-testid="unsupported-copy-link-btn">
            <Trans i18nKey="general.copyLink" />
          </Button>

          <Button variant="secondary" onClick={handleContinue} data-testid="unsupported-continue-btn">
            <Trans i18nKey="general.continue" />
          </Button>
        </div>
      }
    />
  )
}
